'use client';

import { useState } from 'react';
import Link from 'next/link';
import {
  capitalize,
  withIndefiniteArticle
} from '@paction-2.0/util/formatters';
import { buildHealthScreeningSentence } from './faqData';
import styles from './styles/locationFAQ.module.scss';

interface FAQItem {
  question: string;
  answerNode: React.ReactNode;
}

interface PuppiesFAQProps {
  breedName?: string;
  nicknames?: string[] | null;
  breedSlug?: string;
  /** Breed-specific screening names to enrich the breeder-selection answer */
  healthScreenings?: string[] | null;
}

function generateBreedFAQItems(
  breedName: string,
  nicknames?: string[] | null,
  breedSlug?: string,
  healthScreenings?: string[] | null
): FAQItem[] {
  const healthSentence = buildHealthScreeningSentence(
    breedName,
    healthScreenings
  );
  const items: FAQItem[] = [];

  if (nicknames && nicknames.length > 0) {
    const nicknameList = nicknames.slice(0, 5).join(', ');
    items.push({
      question: `What is ${withIndefiniteArticle(breedName)} also called?`,
      answerNode: (
        <p>
          {capitalize(withIndefiniteArticle(breedName))} is also commonly known
          as {nicknameList}. These are popular alternative names and search
          terms used for this breed across Canada.
        </p>
      )
    });
  }

  items.push(
    {
      question: `How much do ${breedName} puppies cost in Canada?`,
      answerNode: (
        <>
          <p>
            {breedName} puppy prices in Canada vary based on breeder reputation,
            health testing, lineage, and breeding rights. Always prioritize
            breeders who provide health guarantees and proper documentation.
          </p>
          <p>
            Beyond the purchase price, budgeting for ongoing costs is equally
            important. Paction has built the{' '}
            <Link href="/dog-cost-calculator">
              Ultimate Dog Cost Calculator
            </Link>{' '}
            to help you plan for the full lifetime cost of your dog.
            {breedSlug && (
              <>
                {' '}
                For a breed-specific breakdown, see the{' '}
                <Link href={`/dog-breeds/${breedSlug}/cost`}>
                  full {breedName} cost guide
                </Link>
                .
              </>
            )}
          </p>
        </>
      )
    },
    {
      question: `Where can I find reputable ${breedName} breeders in Canada?`,
      answerNode: (
        <p>
          You can find reputable {breedName} breeders right here on Paction.
          Paction features verified breeders across Canada who raise healthy,
          well-socialized puppies. Use the search results above to view current
          listings and waitlists, or join the Global Waitlist and Paction will
          connect you with an excellent breeder.
        </p>
      )
    },
    {
      question: `How do I find a responsible ${breedName} breeder?`,
      answerNode: (
        <>
          <p>
            Finding a responsible {breedName} breeder starts with understanding
            how the breeder plans their litters — not just whether the dogs are
            registered. Responsible breeders prioritize genetic and health
            screening of both parents and provide transparent lineage
            information.
          </p>
          <p>
            To understand more about responsible breeding programs, check out{' '}
            <Link href="/canine-genetics-for-puppy-shoppers">
              Making Science Simple: Canine Genetics for Puppy Shoppers
            </Link>
            .
          </p>
        </>
      )
    },
    {
      question: `What should I look for in ${withIndefiniteArticle(breedName)} breeder?`,
      answerNode: (
        <>
          <p>
            When choosing {withIndefiniteArticle(breedName)} breeder, look for
            health testing on parent dogs, health guarantees, early puppy
            socialization, and willingness to answer questions about their
            breeding program.
            {healthSentence && <> {healthSentence}</>}
          </p>
          <p>
            To understand why health testing matters, check out Paction&apos;s{' '}
            <Link href="/canine-genetics-for-puppy-shoppers">
              Making Science Simple: Canine Genetics for Puppy Shoppers
            </Link>
            . For more educational resources, explore the{' '}
            <Link href="/expert-hub">Expert Hub</Link>.
          </p>
        </>
      )
    },
    {
      question: `How can I avoid puppy scams when looking for ${withIndefiniteArticle(breedName)}?`,
      answerNode: (
        <p>
          Puppy scams are common when searching for popular breeds like{' '}
          {breedName}. Red flags include breeders unwilling to show health
          testing, pressure to send deposits quickly, or puppies offered without
          clear planning details. Paction reduces scam risk by working with
          breeders who verify their identities and commit to transparent,
          documented breeding practices.
        </p>
      )
    }
  );

  return items;
}

const GENERIC_FAQ_ITEMS: FAQItem[] = [
  {
    question: 'Where can I find dogs for sale in Canada?',
    answerNode: (
      <p>
        Paction lists dogs for sale from ethical, health-tested breeders across
        Canada. You can search by breed and province to find your ideal
        companion.
      </p>
    )
  },
  {
    question: 'Can I find dogs for adoption on Paction?',
    answerNode: (
      <p>
        Yes. Paction connects you with dogs for adoption from trusted breeders
        across Canada, including puppies that are ready for rehoming. Browse
        current listings or join a waitlist to be notified when a new litter is
        ready.
      </p>
    )
  },
  {
    question: 'Are mixed breed pups available on Paction?',
    answerNode: (
      <p>
        Yes. Alongside purebreds, you can find mixed breed pups from responsible
        breeders across Canada. Use the breed filter or browse all listings to
        explore what is currently available.
      </p>
    )
  },
  {
    question: 'How do I know a breeder on Paction is reputable?',
    answerNode: (
      <p>
        Every breeder on Paction is vetted before joining the platform. Paction
        breeders commit to health testing, transparent practices, and lifetime
        support for the dogs they place. You can read breeder profiles, view
        available puppies, and message breeders directly before making any
        decisions.
      </p>
    )
  },
  {
    question: 'How much does it cost to buy a puppy in Canada?',
    answerNode: (
      <p>
        Puppy prices vary by breed, breeder, and location. Beyond the purchase
        price, budgeting for ongoing costs — vet care, food, grooming, and
        training — is equally important. Paction has built the{' '}
        <Link href="/dog-cost-calculator">Ultimate Dog Cost Calculator</Link> to
        help you plan for the full lifetime cost of your dog.
      </p>
    )
  }
];

export function PuppiesFAQ({
  breedName,
  nicknames,
  breedSlug,
  healthScreenings
}: PuppiesFAQProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const faqItems = breedName
    ? generateBreedFAQItems(breedName, nicknames, breedSlug, healthScreenings)
    : GENERIC_FAQ_ITEMS;

  const toggleItem = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <section className={styles.locationFaq} aria-labelledby="puppies-faq-title">
      <h2 id="puppies-faq-title" className={styles.title}>
        Frequently Asked Questions
      </h2>
      <div className={styles.faqList}>
        {faqItems.map((item, index) => (
          <div
            key={index}
            className={`${styles.faqItem} ${expandedIndex === index ? styles.faqItemExpanded : ''}`}
          >
            <button
              type="button"
              className={styles.questionButton}
              onClick={() => toggleItem(index)}
              aria-expanded={expandedIndex === index}
              aria-controls={`puppies-faq-answer-${index}`}
            >
              <h3 className={styles.question}>{item.question}</h3>
              <span className={styles.chevron} aria-hidden="true" />
            </button>
            <div
              id={`puppies-faq-answer-${index}`}
              className={styles.answer}
              hidden={expandedIndex !== index}
            >
              {item.answerNode}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
